import { RouterProvider } from 'react-router-dom';
import routes from "./routes/Router";
import { useTheme } from "./hooks/useTheme";
import Logo from "./layouts/Logo";

import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import {
    Authenticator,
    Button,
    useAuthenticator,
    TextField,
    PasswordField
} from '@aws-amplify/ui-react';

import {
    signIn,
    signOut,
    confirmSignIn,
    resetPassword,
    confirmResetPassword
} from 'aws-amplify/auth';
import { setDefaultApiConfig, setSelectedImplementationConfig } from './api/runtimeApi';

import { useDispatch, useSelector } from 'react-redux';
import { fetchAuthenticatedUser } from "./features/auth/authenticationSlice";
import DataLoader from './components/loader/DataLoader';

import { useState, useEffect, useRef } from 'react';

import { Spinner } from 'reactstrap';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function getDomain(email) {
    const at = email.lastIndexOf("@");
    return at > 0 ? email.slice(at + 1).trim().toLowerCase() : null;
}

function toDomainArray(value) {
    if (Array.isArray(value)) {
        return value
            .map((domain) => (domain || '').toString().trim().toLowerCase())
            .filter(Boolean);
    }

    if (typeof value === 'string' && value.trim()) {
        return [value.trim().toLowerCase()];
    }

    return [];
}

function parseMappingRecord(record) {
    if (!record || typeof record !== 'object') {
        return null;
    }

    const companyId = record.companyId ?? record.CompanyId;
    const companySlug = record.companySlug ?? record.CompanySlug;
    const domains = toDomainArray(record.domains ?? record.Domains ?? record.domain ?? record.Domain);
    const ssoEnabled = Boolean(record.ssoEnabled ?? record.SSOEnabled);
    const implementation = record.implementation ?? record.Implementation;
    const idpIdentifier = record.idpIdentifier ?? record.IdpIdentifier ?? (domains.length > 0 ? domains[0] : '') ?? '';

    return {
        companyId,
        companySlug,
        domains,
        ssoEnabled,
        implementation,
        idpIdentifier,
    };
}

function extractMappingCandidates(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    if (Array.isArray(payload.results)) {
        return payload.results;
    }

    if (Array.isArray(payload.Results)) {
        return payload.Results;
    }

    if (Array.isArray(payload.items)) {
        return payload.items;
    }

    if (Array.isArray(payload.Items)) {
        return payload.Items;
    }

    return [payload];
}

function findMappingForDomain(payload, domain) {
    const normalizedDomain = (domain || '').trim().toLowerCase();
    const parsedRecords = extractMappingCandidates(payload)
        .map(parseMappingRecord)
        .filter(Boolean);

    const exactMatch = parsedRecords.find((record) => record.domains.includes(normalizedDomain));
    if (exactMatch) {
        return exactMatch;
    }

    return null;
}

function base64url(bytes) {
    const str = btoa(String.fromCharCode(...new Uint8Array(bytes)));
    return str.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function sha256(input) {
    const enc = new TextEncoder().encode(input);
    return crypto.subtle.digest("SHA-256", enc);
}

function randomString(len = 64) {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
    const bytes = crypto.getRandomValues(new Uint8Array(len));
    return Array.from(bytes, b => chars[b % chars.length]).join("");
}

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_AWS_USER_POOLS_ID,
            userPoolClientId: process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID,
            signUpVerificationMethod: 'code',
            loginWith: {
                email: true,
                oauth: {
                    domain: process.env.REACT_APP_AWS_OAUTH_DOMAIN,
                    scopes: ["email", "openid", "profile", "aws.cognito.signin.user.admin"],
                    redirectSignIn: [process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI],
                    redirectSignOut: [process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI],
                    responseType: 'code',
                }
            }
        }
    }
});

// ---------------------------------------------------------------------------
// Styles - card only, page background comes from the theme className in App
// ---------------------------------------------------------------------------
const styles = {
    card: {
        backgroundColor: 'var(--bg-color-contrast1)',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '460px',
        overflow: 'hidden',
    },
    logoWrapper: {
        display: 'flex',
        justifyContent: 'center',
        padding: '2rem 2rem 1rem',
    },
    formBody: {
        padding: '1.5rem 2rem 2rem',
    },
    fieldWrapper: {
        marginBottom: '1rem',
    },
    errorText: {
        color: 'var(--amplify-colors-red-60)',
        fontSize: '0.875rem',
        marginTop: '0.5rem',
    },
    primaryButton: {
        width: '100%',
        marginTop: '1.5rem',
    },
    secondaryActions: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '0.75rem',
    },
    secondaryActionsSingleButton: {
        width: '100%',
        marginTop: '0.75rem'
    }
};

// ---------------------------------------------------------------------------
// Headless login form
// ---------------------------------------------------------------------------
function LoginPage() {
    // keeping these in case you want to use submitForm later, but not required for this flow
    const { isPending } = useAuthenticator((ctx) => [ctx.isPending]);

    const [submitting, setSubmitting] = useState(false);
    const [step, setStep] = useState('email'); // email | password | newPasswordRequired | forgot | reset
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const passwordRef = useRef(null);

    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState(''); // used for BOTH reset + new-password-required (see step)
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const newPasswordError =
        step === 'newPasswordRequired'
            ? (!newPassword ? 'Please enter a new password.' :
                !confirmNewPassword ? 'Please confirm your new password.' :
                    newPassword !== confirmNewPassword ? 'Passwords do not match.' :
                        '')
            : '';

    useEffect(() => {
        if (step === 'password') {
            setTimeout(() => passwordRef.current?.focus?.(), 0);
        }
    }, [step]);

    const redirectToSsoByEmail = async (emailInput, idpIdentifierOverride) => {
        const domain = getDomain(emailInput);
        if (!domain) {
            setError("Invalid email address");
            return;
        }

        const cognitoDomain = process.env.REACT_APP_AWS_OAUTH_DOMAIN;
        const clientId = process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID;
        const redirectUri = process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI;

        if (!cognitoDomain || !clientId || !redirectUri) {
            setError("Authentication configuration error. Please contact support.");
            console.error("Missing required OAuth configuration");
            return;
        }

        if (cognitoDomain.includes("://")) {
            console.error("REACT_APP_AWS_OAUTH_DOMAIN should not include protocol (https://)");
            setError("Authentication configuration error. Please contact support.");
            return;
        }

        const scopes = ["openid", "email", "profile", "aws.cognito.signin.user.admin"];

        // PKCE
        const codeVerifier = randomString(96);
        const codeChallenge = base64url(await sha256(codeVerifier));

        sessionStorage.setItem("pkce_verifier", codeVerifier);
        sessionStorage.setItem("login_email", emailInput);

        const state = randomString(24);
        sessionStorage.setItem("oauth_state", state);

        const url = new URL(`https://${cognitoDomain}/oauth2/authorize`);
        url.searchParams.set("client_id", clientId);
        url.searchParams.set("redirect_uri", redirectUri);
        url.searchParams.set("response_type", "code");
        url.searchParams.set("scope", scopes.join(" "));
        url.searchParams.set("code_challenge_method", "S256");
        url.searchParams.set("code_challenge", codeChallenge);
        url.searchParams.set("state", state);

        url.searchParams.set("idp_identifier", idpIdentifierOverride || domain);

        window.location.assign(url.toString());
    };

    const canGoNext = email.trim().length > 0 && EMAIL_REGEX.test(email);

    const onPrimaryClick = async () => {
        setError('');

        if (step === 'email') {
            if (!canGoNext) {
                setError('Please enter a valid email address.');
                return;
            }

            const emailTrimmed = email.trim();
            const domain = getDomain(emailTrimmed);
            try {
                const response = await fetch(`${process.env.REACT_APP_IDP_MAPPING_API}?domain=${encodeURIComponent(domain)}`);

                if (!response.ok) {
                    setDefaultApiConfig();
                    setStep('password');
                    return;
                }

                const data = await response.json();
                const mapping = findMappingForDomain(data, domain);

                if (!mapping) {
                    setDefaultApiConfig();
                    setStep('password');
                    return;
                }

                if (mapping.domains.length > 0 && !mapping.domains.includes(domain)) {
                    setDefaultApiConfig();
                    setStep('password');
                    return;
                }

                if (!setSelectedImplementationConfig(mapping.implementation)) {
                    setDefaultApiConfig();
                }

                if (!mapping.ssoEnabled) {
                    setStep('password');
                    return;
                }

                try {
                    setSubmitting(true);
                    await redirectToSsoByEmail(emailTrimmed, mapping.idpIdentifier || domain);
                } catch (err) {
                    setError('Failed to initiate SSO login. Please try again.');
                }
                finally {
                    setSubmitting(false);
                }
            } catch {
                setDefaultApiConfig();
                setStep('password');
            }
        }

        if (step === 'password') {
            try {
                setSubmitting(true);
                const res = await signIn({ username: email.trim(), password });

                // If Cognito requires more steps (e.g. NEW_PASSWORD_REQUIRED, MFA), handle here:
                const signInStep = res?.nextStep?.signInStep;

                if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
                    setNewPassword('');
                    setConfirmNewPassword('');
                    setStep('newPasswordRequired');
                    return;
                }

                if (signInStep && signInStep !== 'DONE') {
                    console.log("Unhandled next step:", res?.nextStep);
                    setError(`Additional sign-in step required: ${signInStep}`);
                    return;
                }

                // DONE: Authenticator context should update; AuthGate will render the app
                return;
            } catch (err) {
                console.log("SIGNIN ERROR FULL:", err);
                setError(`${err?.message ?? "Sign in failed"}`);
                return;
            }
            finally {
                setSubmitting(false);
            }
        }

        if (step === 'newPasswordRequired') {
            // client-side validation
            if (!newPassword || !confirmNewPassword) {
                setError('Please enter and confirm your new password.');
                return;
            }
            if (newPassword !== confirmNewPassword) {
                setError('Passwords do not match.');
                return;
            }

            try {
                setSubmitting(true);
                await confirmSignIn({ challengeResponse: newPassword });

                setError('');
                setPassword('');
                setNewPassword('');
                setConfirmNewPassword('');
                return;
            } catch (err) {
                console.log("CONFIRM SIGNIN ERROR FULL:", err);
                setError(`${err?.name ?? "Error"}: ${err?.message ?? "Failed to set new password"}`);
                return;
            }
            finally {
                setSubmitting(false);
            }
        }


        if (step === 'forgot') {
            try {
                setSubmitting(true);
                await resetPassword({ username: email.trim() });
                setStep('reset');
            } catch (err) {
                setError(err?.message ?? 'Failed to send reset code. Please try again.');
            }
            finally {
                setSubmitting(false);
            }
            return;
        }

        if (step === 'reset') {
            try {
                setSubmitting(true);
                await confirmResetPassword({
                    username: email.trim(),
                    confirmationCode: code,
                    newPassword
                });

                // Reset complete - go back to password step to sign in
                setStep('password');
                setCode('');
                setNewPassword('');
                setError('');
            } catch (err) {
                setError(err?.message ?? 'Failed to reset password. Please try again.');
            }
            finally {
                setSubmitting(false);
            }
            return;
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') onPrimaryClick();
    };

    const primaryButtonLabel =
        step === 'email' ? 'Next' :
            step === 'password' ? 'Sign in' :
                step === 'newPasswordRequired' ? 'Set new password' :
                    step === 'forgot' ? 'Send reset code' :
                        step === 'reset' ? 'Reset password' :
                            'Continue';

    return (
        <div
            style={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <div className="d-flex" style={{ flexDirection: 'column' }}>
                <div style={styles.logoWrapper}>
                    <Logo />
                </div>

                <div style={styles.card}>
                    <div style={styles.formBody}>
                        {/* Email */}
                        {step !== 'newPasswordRequired' &&
                            <div style={step !== 'email' && step !== 'forgot' ? styles.fieldWrapper : undefined}>
                                <TextField
                                    label="Email"
                                    name="username"
                                    type="email"
                                    placeholder="Enter your email..."
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={step === 'email' ? onKeyDown : undefined}
                                    isReadOnly={step !== 'email' && step !== 'forgot'}
                                    autoComplete="username"
                                />
                            </div>
                        }

                        {/* Password */}
                        {step === 'password' && (
                            <div>
                                <PasswordField
                                    label="Password"
                                    name="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={onKeyDown}
                                    ref={passwordRef}
                                    autoComplete="current-password"
                                />
                            </div>
                        )}

                        {step === 'newPasswordRequired' && (
                            <>
                                <h2 style={{ margin: 0, marginBottom: '1.25rem', fontSize: '1.5rem' }}>
                                    Change Password
                                </h2>

                                <div style={styles.fieldWrapper}>
                                    <PasswordField
                                        label="Password"
                                        name="newPassword"
                                        placeholder="Enter your Password"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        onKeyDown={onKeyDown}
                                        autoComplete="new-password"
                                    />
                                </div>

                                <div style={styles.fieldWrapper}>
                                    <PasswordField
                                        label="Confirm Password"
                                        name="confirmNewPassword"
                                        placeholder="Please confirm your Password"
                                        value={confirmNewPassword}
                                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                                        onKeyDown={onKeyDown}
                                        autoComplete="new-password"
                                    />
                                </div>

                                {newPasswordError && (
                                    <p style={styles.errorText}>{newPasswordError}</p>
                                )}
                            </>
                        )}


                        {/* Forgot password reset step */}
                        {step === 'reset' && (
                            <>
                                <div style={styles.fieldWrapper}>
                                    <TextField
                                        label="Reset code"
                                        name="code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        autoComplete="one-time-code"
                                    />
                                </div>
                                <div style={styles.fieldWrapper}>
                                    <PasswordField
                                        label="New password"
                                        name="newPassword"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        autoComplete="new-password"
                                    />
                                </div>
                            </>
                        )}

                        {error && (
                            <p style={styles.errorText}>{error}</p>
                        )}

                        <Button
                            type="button"
                            variation="primary"
                            isLoading={isPending}
                            onClick={onPrimaryClick}
                            style={{ ...styles.primaryButton, opacity: submitting ? '0.4' : '1' }}
                            disabled={submitting}
                        >
                            <div className="d-flex align-items-center flex-row gap-2">
                                {submitting && <Spinner size="sm" />}
                                <span>
                                    {primaryButtonLabel}
                                </span>
                            </div>
                        </Button>

                        {/* Secondary actions */}
                        {step !== 'email' && (
                            <div style={step !== 'forgot' && step !== 'reset' && step !== 'newPasswordRequired' ? styles.secondaryActions : undefined}>
                                <Button
                                    style={(step === 'forgot' || step === 'reset' || step === 'newPasswordRequired') ? styles.secondaryActionsSingleButton : undefined}
                                    type="button"
                                    variation="link"
                                    onClick={() => {
                                        setError('');

                                        if (step === 'password') {
                                            setStep('email');
                                            setPassword('');
                                            return;
                                        }

                                        if (step === 'newPasswordRequired') {
                                            // go back to password entry (user can retry)
                                            setStep('password');
                                            setNewPassword('');
                                            return;
                                        }

                                        if (step === 'forgot') {
                                            setStep('password');
                                            return;
                                        }

                                        if (step === 'reset') {
                                            setStep('forgot');
                                            return;
                                        }
                                    }}
                                >
                                    Back
                                </Button>

                                {step === 'password' && (
                                    <Button
                                        type="button"
                                        variation="link"
                                        onClick={() => { setStep('forgot'); setError(''); }}
                                    >
                                        Forgot your password?
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Auth gate - renders login or app based on auth and profile state
// ---------------------------------------------------------------------------
function LoggingInScreen({ message }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', width: '100%' }}>
            <DataLoader message={message} />
        </div>
    );
}

function AuthGate({ dispatch }) {
    const routing = routes();
    const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
    const authFetchStatus = useSelector((state) => state.authentication.status);
    const authError = useSelector((state) => state.authentication.error);

    useEffect(() => {
        if (authStatus === 'authenticated') {
            dispatch(fetchAuthenticatedUser());
        }
    }, [authStatus, dispatch]);

    useEffect(() => {
        if (authStatus === 'authenticated' && authFetchStatus === 'failed' && authError?.type === 'missing_company') {
            signOut().catch(() => {
                console.error('SignOut Failed: Error redirecting to login');
            });
        }
    }, [authStatus, authFetchStatus, authError]);

    if (authStatus === 'configuring') return null;

    if (authStatus !== 'authenticated') {
        return <LoginPage />;
    }

    if (authFetchStatus === 'idle' || authFetchStatus === 'loading') {
        return <LoggingInScreen message="Verifying your access..." />;
    }

    if (authFetchStatus === 'failed' && authError?.type === 'missing_company') {
        return <LoggingInScreen message="Unable to load company claim. Redirecting to login..." />;
    }

    if (authFetchStatus === 'failed') {
        return <LoggingInScreen message="Authentication failed. Please sign in again." />;
    }

    if (authFetchStatus !== 'succeeded') {
        return <LoggingInScreen message="Verifying your access..." />;
    }

    return <RouterProvider router={routing} />;
}

// ---------------------------------------------------------------------------
// App root - theme className drives the page background colour
// ---------------------------------------------------------------------------
export default function App() {
    const theme = useTheme();
    const dispatch = useDispatch();

    useEffect(() => {
        const unlisten = Hub.listen('auth', (data) => {
            switch (data?.payload?.event) {
                case 'signIn':
                    console.log('User signed in');
                    break;
                case 'cognitoHostedUI':
                    console.log('Authenticated via hosted UI');
                    break;
                case 'signIn_failure':
                case 'cognitoHostedUI_failure':
                    console.error('Auth error:', data);
                    break;
                default:
                    console.log('Auth event:', data?.payload?.event);
            }
        });
        return () => unlisten();
    }, []);

    // In your App component, add this useEffect BEFORE Authenticator.Provider renders
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const exchangeGuardKey = 'oauth_exchange_code';

        if (!code) return;

        const storedState = sessionStorage.getItem('oauth_state');
        const codeVerifier = sessionStorage.getItem('pkce_verifier');

        if (sessionStorage.getItem(exchangeGuardKey) === code) {
            console.warn('OAuth code exchange already attempted for this code; skipping duplicate exchange.');
            window.history.replaceState({}, document.title, '/');
            return;
        }

        if (state !== storedState) {
            console.error('OAuth state mismatch');
            window.history.replaceState({}, document.title, '/');
            return;
        }

        if (!codeVerifier) {
            console.error('Missing PKCE code verifier for OAuth token exchange');
            window.history.replaceState({}, document.title, '/');
            return;
        }

        // Guard against duplicate exchanges in dev StrictMode/remount scenarios.
        sessionStorage.setItem(exchangeGuardKey, code);
        // Remove callback params immediately so remounts cannot reuse a single-use code.
        window.history.replaceState({}, document.title, '/');

        const cognitoDomain = process.env.REACT_APP_AWS_OAUTH_DOMAIN;
        const clientId = process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID;
        const redirectUri = process.env.REACT_APP_AWS_OAUTH_REDIRECT_URI;

        fetch(`https://${cognitoDomain}/oauth2/token`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'authorization_code',
                client_id: clientId,
                code,
                redirect_uri: redirectUri,
                code_verifier: codeVerifier,
            }),
        })
            .then(r => r.json())
            .then(tokens => {
                if (tokens.error) {
                    console.error('Token exchange failed:', tokens);
                    return;
                }

                // Store tokens where Amplify v6 expects them
                const keyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
                const email = sessionStorage.getItem('login_email') ?? 'sso-user';

                localStorage.setItem(`${keyPrefix}.LastAuthUser`, email);
                localStorage.setItem(`${keyPrefix}.${email}.idToken`, tokens.id_token);
                localStorage.setItem(`${keyPrefix}.${email}.accessToken`, tokens.access_token);
                localStorage.setItem(`${keyPrefix}.${email}.refreshToken`, tokens.refresh_token);
                localStorage.setItem(`${keyPrefix}.${email}.clockDrift`, '0');

                // Clean up
                sessionStorage.removeItem('pkce_verifier');
                sessionStorage.removeItem('oauth_state');
                sessionStorage.removeItem('login_email');
                sessionStorage.removeItem(exchangeGuardKey);

                // Remove code from URL and reload so Amplify picks up the stored tokens
                window.location.reload();
            })
            .catch(err => {
                console.error('Token exchange error:', err);
            });
    }, []);

    return (
        <div
            className={theme}
            style={{
                minHeight: '100vh'
            }}
        >
            <Authenticator.Provider>
                <AuthGate dispatch={dispatch} />
            </Authenticator.Provider>
        </div>
    );
}